/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";
import _startsWithInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/starts-with";
import _Set from "@babel/runtime-corejs3/core-js-stable/set";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _Map from "@babel/runtime-corejs3/core-js-stable/map";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/utils/form */
// TODO: ADD SUPPORT FOR SERVER SIDE VALIDATION INTEGRATION.
// TODO: CHECK/IMPROVE ALL VARIABLE/TYPES NAMES IN ALL FORM/INPUT FLOW.
// TODO: CHECK/IMPROVE ALL JSDOC IN ALL FORM/INPUT FLOW.
import { log } from '@codistica/core';
import React from 'react';
import resetClassNames from '../css/reset.module.scss';
import { getRefHandler } from '../modules/get-ref-handler.js';
import { mergeClassNames } from '../modules/merge-class-names.js';
import { mergeStyles } from '../modules/merge-styles.js';
import { InputContext } from './input-renderer.js';

/**
 * @typedef formPropsType
 * @property {Function} [onValidationResult=null] - Callback for validationResult event.
 * @property {Function} [onMount=null] - Callback for componentDidMount event.
 * @property {*} [children=null] - React prop.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @classdesc A beautiful form component.
 */
var Form = /*#__PURE__*/function (_React$Component) {
  _inherits(Form, _React$Component);

  var _super = _createSuper(Form);

  /**
   * @description Constructor.
   * @param {formPropsType} [props] - Component props.
   */
  function Form(props) {
    var _context, _context2, _context3, _context4, _context5, _context6, _context7, _context8, _context9, _context10, _context11;

    var _this;

    _classCallCheck(this, Form);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "registeredInputs", void 0);

    _defineProperty(_assertThisInitialized(_this), "validationResult", void 0);

    _defineProperty(_assertThisInitialized(_this), "dataPayload", void 0);

    _defineProperty(_assertThisInitialized(_this), "formValidationObject", void 0);

    _defineProperty(_assertThisInitialized(_this), "linkedInputsMap", void 0);

    _defineProperty(_assertThisInitialized(_this), "formRef", void 0);

    _defineProperty(_assertThisInitialized(_this), "inputContextValue", void 0);

    _this.registeredInputs = {};
    _this.validationResult = false;
    _this.dataPayload = {};
    _this.formValidationObject = {};
    _this.linkedInputsMap = new _Map();
    _this.formRef = {
      current: null
    }; // BIND METHODS

    _assertThisInitialized(_this).registerInput = _bindInstanceProperty(_context = _this.registerInput).call(_context, _assertThisInitialized(_this));
    _assertThisInitialized(_this).unregisterInput = _bindInstanceProperty(_context2 = _this.unregisterInput).call(_context2, _assertThisInitialized(_this));
    _assertThisInitialized(_this).linkInputs = _bindInstanceProperty(_context3 = _this.linkInputs).call(_context3, _assertThisInitialized(_this));
    _assertThisInitialized(_this).unlinkInput = _bindInstanceProperty(_context4 = _this.unlinkInput).call(_context4, _assertThisInitialized(_this));
    _assertThisInitialized(_this).validateLinkedInputs = _bindInstanceProperty(_context5 = _this.validateLinkedInputs).call(_context5, _assertThisInitialized(_this));
    _assertThisInitialized(_this).checkMatch = _bindInstanceProperty(_context6 = _this.checkMatch).call(_context6, _assertThisInitialized(_this));
    _assertThisInitialized(_this).validateForm = _bindInstanceProperty(_context7 = _this.validateForm).call(_context7, _assertThisInitialized(_this));
    _assertThisInitialized(_this).warnInvalids = _bindInstanceProperty(_context8 = _this.warnInvalids).call(_context8, _assertThisInitialized(_this));
    _assertThisInitialized(_this).clear = _bindInstanceProperty(_context9 = _this.clear).call(_context9, _assertThisInitialized(_this));
    _assertThisInitialized(_this).getInputElementByName = _bindInstanceProperty(_context10 = _this.getInputElementByName).call(_context10, _assertThisInitialized(_this));
    _assertThisInitialized(_this).getInputByName = _bindInstanceProperty(_context11 = _this.getInputByName).call(_context11, _assertThisInitialized(_this));
    _this.inputContextValue = {
      formInstance: _assertThisInitialized(_this)
    };
    return _this;
  }
  /**
   * @instance
   * @description React lifecycle.
   * @returns {void} Void.
   */


  _createClass(Form, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.props.onMount && this.props.onMount(this);
    }
    /**
     * @instance
     * @description Register input in the form component.
     * @param {InputRenderer} input - Input instance.
     * @returns {void} Void.
     */

  }, {
    key: "registerInput",
    value: function registerInput(input) {
      if (this.getInputByName(input.props.name)) {
        log.warning('registerInput()', 'INPUT ALREADY REGISTERED')();
      }

      this.registeredInputs[input.id] = input;
    }
    /**
     * @instance
     * @description Unregister input from the form component.
     * @param {InputRenderer} input - Input instance.
     * @returns {void} Void.
     */

  }, {
    key: "unregisterInput",
    value: function unregisterInput(input) {
      delete this.registeredInputs[input.id];
    }
    /**
     * @instance
     * @description Link inputs for parallel validation.
     * @param {...string} inputsNames - Inputs names.
     * @returns {void} Void.
     */

  }, {
    key: "linkInputs",
    value: function linkInputs() {
      var _this2 = this,
          _context12;

      for (var _len = arguments.length, inputsNames = new Array(_len), _key = 0; _key < _len; _key++) {
        inputsNames[_key] = arguments[_key];
      }

      _forEachInstanceProperty(inputsNames).call(inputsNames, _bindInstanceProperty(_context12 = function _context12(inputNameA, indexA) {
        var _this3 = this,
            _context13;

        _newArrowCheck(this, _this2);

        var linkedInputsSet = this.linkedInputsMap.get(inputNameA) || new _Set();

        _forEachInstanceProperty(inputsNames).call(inputsNames, _bindInstanceProperty(_context13 = function _context13(inputNameB, indexB) {
          _newArrowCheck(this, _this3);

          if (indexB !== indexA) {
            linkedInputsSet.add(inputNameB);
          }
        }).call(_context13, this));

        this.linkedInputsMap.set(inputNameA, linkedInputsSet);
      }).call(_context12, this));
    }
    /**
     * @instance
     * @description Unlink inputs.
     * @param {string} inputName - Input name.
     * @returns {void} Void.
     */

  }, {
    key: "unlinkInput",
    value: function unlinkInput(inputName) {
      var _context14,
          _this4 = this,
          _context15;

      _forEachInstanceProperty(_context14 = this.linkedInputsMap).call(_context14, _bindInstanceProperty(_context15 = function _context15(linkedInputsSet, currentInputName) {
        _newArrowCheck(this, _this4);

        if (currentInputName !== inputName) {
          linkedInputsSet.delete(inputName);
        }
      }).call(_context15, this));

      this.linkedInputsMap.delete(inputName);
    }
    /**
     * @instance
     * @description Trigger linked inputs validation.
     * @param {string} inputName - Input name.
     * @returns {void} Void.
     */

  }, {
    key: "validateLinkedInputs",
    value: function validateLinkedInputs(inputName) {
      var _this5 = this;

      var linkedInputsSet = this.linkedInputsMap.get(inputName);

      if (linkedInputsSet) {
        var _context16;

        _forEachInstanceProperty(linkedInputsSet).call(linkedInputsSet, _bindInstanceProperty(_context16 = function _context16(linkedInputName) {
          _newArrowCheck(this, _this5);

          var linkedInput = this.getInputByName(linkedInputName);

          if (linkedInput) {
            linkedInput.validateInput();
          }
        }).call(_context16, this));
      }
    }
    /**
     * @instance
     * @description Checks input based on its matching rules prop.
     * @param {string} inputName - Input name.
     * @returns {(boolean|null)} Result.
     */

  }, {
    key: "checkMatch",
    value: function checkMatch(inputName) {
      var result = null;
      var input = this.getInputByName(inputName);

      if (input && input.props.match) {
        var matchProp = input.props.match;
        var matchName = matchProp.replace(/^!/, '');

        var invert = _startsWithInstanceProperty(matchProp).call(matchProp, '!');

        var matchedInput = this.getInputByName(matchName);

        if (matchedInput) {
          if (invert) {
            return matchedInput.state.value !== input.state.value;
          }

          return matchedInput.state.value === input.state.value;
        }
      }

      return result;
    }
    /**
     * @instance
     * @description Validates entire form based on registered inputs data.
     * @returns {void} Void.
     */

  }, {
    key: "validateForm",
    value: function validateForm() {
      this.validationResult = true;

      for (var i in this.registeredInputs) {
        if (!Object.prototype.hasOwnProperty.call(this.registeredInputs, i)) {
          continue;
        }

        var input = this.registeredInputs[i]; // CHECK VALIDITY

        if (input.validationObject.result === false) {
          this.validationResult = false;
        } // COLLECT INPUT INFORMATION


        this.dataPayload[input.props.name] = input.state.value;
        this.formValidationObject[input.props.name] = input.validationObject;
      }

      this.props.onValidationResult && this.props.onValidationResult(this.validationResult, this.dataPayload, this.formValidationObject);
    }
    /**
     * @instance
     * @description Triggers warning highlighting on invalid inputs.
     * @returns {void} Void.
     */

  }, {
    key: "warnInvalids",
    value: function warnInvalids() {
      var focused = false;

      for (var i in this.registeredInputs) {
        if (!Object.prototype.hasOwnProperty.call(this.registeredInputs, i)) {
          continue;
        }

        var input = this.registeredInputs[i]; // INDICATE THAT THERE HAS BEEN INTERACTION

        if (!input.hasInteracted) {
          input.hasInteracted = true;
          input.validateInput();
        } // CHECK VALIDITY


        if (input.validationObject.result === false) {
          if (!focused) {
            // FOCUS FIRST INVALID INPUT
            var inputElement = this.getInputElementByName(input.props.name);

            if (inputElement) {
              inputElement.focus();
              focused = true;
            }
          }

          input.warn();
        }
      }
    }
    /**
     * @instance
     * @description Clears form or single input if specified.
     * @param {string} inputName - Name of input to be cleared.
     * @returns {void} Void.
     */

  }, {
    key: "clear",
    value: function clear(inputName) {
      if (inputName) {
        var input = this.getInputByName(inputName);

        if (input) {
          input.clear();
        }

        return;
      }

      for (var i in this.registeredInputs) {
        if (!Object.prototype.hasOwnProperty.call(this.registeredInputs, i)) {
          continue;
        }

        this.registeredInputs[i].clear();
      }
    }
    /**
     * @instance
     * @description Searches input element by name.
     * @param {string} inputName - Input name.
     * @returns {(HTMLInputElement|null)} Found inputs array.
     */

  }, {
    key: "getInputElementByName",
    value: function getInputElementByName(inputName) {
      return this.formRef.current && this.formRef.current.querySelector("input[name=".concat(inputName, "]"));
    }
    /**
     * @instance
     * @description Search registered inputs and returns first matched name input or null.
     * @param {string} inputName - Input name.
     * @returns {(InputRenderer|null)} Found input or null.
     */

  }, {
    key: "getInputByName",
    value: function getInputByName(inputName) {
      for (var i in this.registeredInputs) {
        if (!Object.prototype.hasOwnProperty.call(this.registeredInputs, i)) {
          continue;
        }

        if (this.registeredInputs[i].props.name === inputName) {
          return this.registeredInputs[i];
        }
      }

      return null;
    }
    /**
     * @instance
     * @description Form submission utility.
     * @param {Function} handler - Submission handler.
     * @returns {void} Void.
     */

  }, {
    key: "submit",
    value: function submit(handler) {
      for (var i in this.registeredInputs) {
        if (!Object.prototype.hasOwnProperty.call(this.registeredInputs, i)) {
          continue;
        }

        var input = this.registeredInputs[i]; // INDICATE THAT THERE HAS BEEN INTERACTION

        if (!input.hasInteracted) {
          input.hasInteracted = true;
          input.validateInput();
        }
      }

      handler(this.validationResult, this.dataPayload, this.formValidationObject);
    }
    /**
     * @instance
     * @description React render method.
     * @returns {Object<string,*>} React component.
     */

  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
          onValidationResult = _this$props.onValidationResult,
          onMount = _this$props.onMount,
          children = _this$props.children,
          style = _this$props.style,
          className = _this$props.className,
          customStyles = _this$props.customStyles,
          customClassNames = _this$props.customClassNames,
          globalTheme = _this$props.globalTheme,
          other = _objectWithoutProperties(_this$props, ["onValidationResult", "onMount", "children", "style", "className", "customStyles", "customClassNames", "globalTheme"]);

      var globalStyles = globalTheme ? Form.globalStyles[globalTheme] || {} : {};
      var globalClassNames = globalTheme ? Form.globalClassNames[globalTheme] || {} : {};
      var mergedStyles = {
        root: mergeStyles(globalStyles.root, customStyles.root, style)
      };
      var mergedClassNames = {
        root: mergeClassNames(resetClassNames.root, globalClassNames.root, customClassNames.root, className)
      };
      return /*#__PURE__*/React.createElement("form", _extends({}, other, {
        ref: getRefHandler(null, this.formRef),
        style: mergedStyles.root,
        className: mergedClassNames.root
      }), /*#__PURE__*/React.createElement(InputContext.Provider, {
        value: this.inputContextValue
      }, children));
    }
  }]);

  return Form;
}(React.Component);

_defineProperty(Form, "globalStyles", {
  default: {
    root: {}
  }
});

_defineProperty(Form, "globalClassNames", {
  default: {
    root: ''
  }
});

_defineProperty(Form, "defaultProps", {
  onValidationResult: null,
  onMount: null,
  children: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
});

export { Form };